import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Avatar, Box, Button, CircularProgress, Stack, TextField, Typography, IconButton, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { searchMembers } from '../../controllers/memberManagementController';
import { addMemberToGroup } from '../../controllers/groupManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';

const AddMemberBar = ({ groupId, onAdded }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setOptions([]); return; }
    let active = true;
    setLoading(true);
    searchMembers(query)
      .then((data) => { if (active) setOptions(data || []); })
      .catch(() => { if (active) setOptions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query]);

  const canAdd = useMemo(() => Boolean(groupId && selected && selected.length > 0), [groupId, selected]);

  const handleAdd = async () => {
    if (!canAdd) return;
    setAdding(true);
    try {
      // Tüm seçili üyeleri ekle
      const addPromises = selected.map((member) => 
        addMemberToGroup({ groupId, memberId: member.id })
      );
      
      const results = await Promise.allSettled(addPromises);
      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const errorCount = results.filter((r) => r.status === 'rejected').length;
      
      if (successCount > 0) {
        toast.success(`${successCount} üye gruba eklendi${errorCount > 0 ? `, ${errorCount} üye eklenemedi` : ''}`);
      } else {
        toast.error('Üyeler eklenemedi');
      }
      
      setSelected([]);
      setQuery('');
      setOptions([]);
      onAdded?.();
    } catch (e) {
      toast.error('Üyeler eklenemedi');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Autocomplete
        multiple
        sx={{ flex: 1 }}
        options={options}
        getOptionLabel={(o) => o.fullName || ''}
        value={selected}
        onChange={(e, v) => setSelected(v)}
        onInputChange={(e, v) => setQuery(v)}
        loading={loading}
        size="small"
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
            {option.fullName}
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.fullName}
              avatar={<Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 20, height: 20 }} />}
              size="small"
              sx={{
                backgroundColor: '#E7EDF4',
                color: '#0E4D94',
                fontSize: '11px',
                height: '24px',
                '& .MuiChip-deleteIcon': {
                  fontSize: '14px',
                  color: '#0E4D94',
                  '&:hover': {
                    color: '#0a3d7a',
                  },
                },
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selected.length > 0 ? `${selected.length} üye seçildi` : 'Üye ara ve ekle...'}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '5px',
                backgroundColor: '#fafafa',
                minHeight: '36px',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0E4D94',
                  },
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0E4D94',
                    borderWidth: '1px',
                  },
                },
              },
              '& .MuiAutocomplete-inputRoot': {
                py: selected.length > 0 ? '2px' : '4px',
                px: '10px',
              },
              '& .MuiAutocomplete-input': {
                py: 0,
                px: 0,
                fontSize: '13px',
              },
              '& .MuiInputBase-input': {
                py: 0.8,
                px: 1,
                fontSize: '13px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e6e6e6',
                borderWidth: '1px',
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <IconButton
        onClick={handleAdd}
        disabled={!canAdd || adding}
        sx={{
          backgroundColor: canAdd && !adding ? '#0E4D94' : '#e6e6e6',
          color: canAdd && !adding ? '#ffffff' : '#a3a3a3',
          borderRadius: '5px',
          width: 36,
          height: 36,
          mt: 0.5,
          '&:hover': {
            backgroundColor: canAdd && !adding ? '#0a3d7a' : '#e6e6e6',
          },
          '&:disabled': {
            backgroundColor: '#e6e6e6',
            color: '#a3a3a3',
          },
        }}
      >
        {adding ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 18 }} />}
      </IconButton>
    </Stack>
  );
};

export default AddMemberBar;


